import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { timeout } from 'rxjs/operators';
import { ApiService } from '../services/api.service';
import { NavigationService } from '../services/navigation.service';
import { PlatformService } from '../native/platform.service';

export const loginGuard = () => {
  const apiService = inject(ApiService);
  const navigationService = inject(NavigationService);
  const router = inject(Router);
  const platform = inject(PlatformService);
  const isNative = platform.isNative;

  // Check if user has valid token
  const token = apiService.getCurrentAuthToken();
  const currentUser = apiService.getCurrentUser();

  if (token && currentUser?.id) {
    // Add a flag to prevent multiple token validations
    const tokenValidationKey = 'login_guard_validation_in_progress';
    if (localStorage.getItem(tokenValidationKey)) {
      // Token validation already in progress, allow access to login page
      return true;
    }

    // Set flag to prevent multiple validations
    localStorage.setItem(tokenValidationKey, 'true');

    // Validate token by making a test API call with timeout
    return new Promise<boolean>((resolve) => {
      const tokenValidationTimeout = setTimeout(() => {
        // Slow network must not sign the browser out
        localStorage.removeItem(tokenValidationKey);
        resolve(true);
      }, 8000); // Reduced to 8 seconds for faster response

      apiService.getUserById(currentUser.id).pipe(
        timeout(8000)
      ).subscribe({
        next: (userData) => {
          clearTimeout(tokenValidationTimeout);
          localStorage.removeItem(tokenValidationKey);
          // Token is valid, redirect to dashboard

          // Use navigation service to get the appropriate route based on role
          const navigationId = navigationService.getRoleBasedNavigationId();
          const organizationId = apiService.getCurrentOrganizationId();
          const userPov = sessionStorage.getItem('user_pov'); // Check sessionStorage directly to handle 'none'
          const urlPrefix = userPov ? userPov.toLowerCase() : (apiService.getUserPov() || '').toLowerCase();

          // Check if user is super admin or admin
          const userInfoStr = localStorage.getItem('user_info');
          let isSuperAdmin = false;
          let isAdmin = false;
          let parsedUser: any = null;
          if (userInfoStr) {
            try {
              parsedUser = JSON.parse(userInfoStr);
              const userRole = parsedUser.role_id || parsedUser.role?.name || parsedUser.type;
              const normalizedRole = String(userRole || '').toLowerCase();
              const userType = String(parsedUser.type || '').toLowerCase();
              if (normalizedRole === 'super admin' || normalizedRole === 'super_admin' ||
                  userType === 'super admin' || userType === 'super_admin' || userType === 'superadmin' ||
                  parsedUser.email === 'system_admin@kormoan.com') {
                isSuperAdmin = true;
              }
              if (normalizedRole === 'admin' || userType === 'admin') {
                isAdmin = true;
              }
            } catch (e) { }
          }

          const freshUser = (userData as any)?.data || userData || {};
          const mergedUser = { ...parsedUser, ...freshUser };

          let validOrgId: string | null = null;
          if (mergedUser.organization && typeof mergedUser.organization === 'object') {
            validOrgId = mergedUser.organization.id || mergedUser.organization._id || null;
          } else if (typeof mergedUser.organization === 'string' && mergedUser.organization !== 'null' && mergedUser.organization !== 'undefined') {
            validOrgId = mergedUser.organization;
          }
          if (!validOrgId) {
            validOrgId = mergedUser.organization_id || mergedUser.org_id || mergedUser.organizationId || null;
          }
          if (!validOrgId) {
            validOrgId = sessionStorage.getItem('organization_id') || localStorage.getItem('organization_id') ||
                         sessionStorage.getItem('org_id') || localStorage.getItem('org_id') || null;
          }
          if (validOrgId === 'null' || validOrgId === 'undefined' || (typeof validOrgId === 'string' && validOrgId.trim() === '')) {
            validOrgId = null;
          }

          const rawParkings = mergedUser.parkings || mergedUser.parkingIds || [];
          const assignedParkings = Array.isArray(rawParkings) ? rawParkings.filter((p: any) => {
            const pId = typeof p === 'object' ? (p?.id || p?.parking_id || p?._id) : p;
            return !!(pId && pId !== 'null' && pId !== 'undefined' && String(pId).trim().length > 0);
          }) : [];

          // Super admin doesn't need organization_id
          // Super admin case
          if (isSuperAdmin) {
            if (isNative) {
              const selectedP = sessionStorage.getItem('parking_id') || localStorage.getItem('parking_id');
              if (selectedP) {
                router.navigate(['/parking', selectedP, 'pos-overview'], { replaceUrl: true });
              } else {
                router.navigate(['/pos-overview'], { replaceUrl: true });
              }
            } else {
              router.navigate(['/dashboard'], { replaceUrl: true });
            }
          } else if (!isAdmin && !isSuperAdmin) {
            const hasAssignedParking = assignedParkings.length > 0;
            const hasOrg = !!validOrgId;

            // Priority 1: Agar org_id null/missing ho, to org wala page aayega (/no-organization)
            if (!hasOrg) {
              sessionStorage.removeItem('organization_id');
              localStorage.removeItem('organization_id');
              sessionStorage.removeItem('org_id');
              localStorage.removeItem('org_id');
              sessionStorage.removeItem('parking_id');
              sessionStorage.removeItem('parkingId');
              localStorage.removeItem('parking_id');
              localStorage.removeItem('parkingId');
              router.navigate(['/no-organization'], { replaceUrl: true });
            } else if ((urlPrefix === 'parking' || parsedUser?.type === 'vendor' || parsedUser?.type === 'operator') && !hasAssignedParking) {
              sessionStorage.removeItem('parking_id');
              sessionStorage.removeItem('parkingId');
              localStorage.removeItem('parking_id');
              localStorage.removeItem('parkingId');
              router.navigate(['/no-parking'], { replaceUrl: true });
            } else if (hasAssignedParking) {
              const pId = typeof assignedParkings[0] === 'object' ? (assignedParkings[0]?.id || assignedParkings[0]?.parking_id) : assignedParkings[0];
              if (isNative) {
                router.navigate(['/parking', pId, 'pos-overview'], { replaceUrl: true });
              } else {
                router.navigate(['/parking', pId, 'dashboard'], { replaceUrl: true });
              }
            } else if (validOrgId && urlPrefix !== 'parking') {
              if (isNative) {
                router.navigate(['/pos-overview'], { replaceUrl: true });
              } else {
                router.navigate([urlPrefix, validOrgId, 'dashboard'], { replaceUrl: true });
              }
            } else {
              router.navigate(['/no-parking'], { replaceUrl: true });
            }
          } else if (userPov === 'none') {
            if (isNative) {
              const pId = sessionStorage.getItem('parking_id') || localStorage.getItem('parking_id');
              if (pId) {
                router.navigate(['/parking', pId, 'pos-overview'], { replaceUrl: true });
              } else {
                router.navigate(['/pos-overview'], { replaceUrl: true });
              }
            } else {
              router.navigate(['/dashboard'], { replaceUrl: true });
            }
          } else if (navigationId) {
            if (isNative) {
              router.navigate(['/parking', navigationId, 'pos-overview'], { replaceUrl: true });
            } else {
              router.navigate(['/parking', navigationId, 'dashboard'], { replaceUrl: true });
            }
          } else if (validOrgId && urlPrefix !== 'parking') {
            if (isNative) {
              const pId = sessionStorage.getItem('parking_id') || localStorage.getItem('parking_id');
              router.navigate(pId ? ['/parking', pId, 'pos-overview'] : ['/pos-overview'], { replaceUrl: true });
            } else {
              router.navigate([urlPrefix, validOrgId, 'dashboard'], { replaceUrl: true });
            }
          } else if (validOrgId) {
            if (isNative) {
              const pId = sessionStorage.getItem('parking_id') || localStorage.getItem('parking_id');
              if (pId) {
                router.navigate(['/parking', pId, 'pos-overview'], { replaceUrl: true });
              } else {
                router.navigate(['/pos-overview'], { replaceUrl: true });
              }
            } else {
              router.navigate([urlPrefix || 'organization', validOrgId, 'dashboard'], { replaceUrl: true });
            }
          } else {
            if (urlPrefix === 'organization' || urlPrefix === 'org' || urlPrefix === 'admin') {
              router.navigate(['/no-organization'], { replaceUrl: true });
            } else {
              router.navigate(['/no-parking'], { replaceUrl: true });
            }
          }
          resolve(false); // Don't allow access to login page
        },
        error: (error) => {
          clearTimeout(tokenValidationTimeout);
          localStorage.removeItem(tokenValidationKey);
          const status = Number(error?.status ?? 0);
          if (status === 401) {
            apiService.clearSessionState({ preservePreferences: true });
          }
          resolve(true);
        }
      });
    });
  } else {
    return true; // Allow access to login page
  }
};
